import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Layout } from "../../../containers/layout/dashboard";
import { ProfileWrapper } from "./styled";
import { BaseButton } from "../../../components/button/styled";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { BaseLegend } from "../../../components/formfields/legend/styled";
import { BaseLabel } from "../../../components/formfields/label/styled";
import { BaseFieldSet } from "../../../components/formfields/fieldset/styled";
import { BaseInput } from "../../../components/formfields/input/styled";
import { retrieveUserProfileService } from "../../../utils/apis/usermanagement/user/retrieveUser";
import Cookies from "universal-cookie";
import { updateUserProfileService } from "../../../utils/apis/usermanagement/user/updateUserProfile";
import { BaseAlertModal } from "../../../components/modals/alert";
import uploadImageIcon from "../../../assets/uploadimageicon.svg";

export const Profile = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState<Record<string, any>>({
        avatar: "",
        firstName: "",
        lastName: "",
        id: "",
        email: "",
        phone: "",
    });
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) {
                    setImagePreview(e.target.result as string);
                    setFormDetails((prev) => ({
                        ...prev,
                        avatar: file
                    }))
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    };

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    }

    const handleNavigateToDashboard = () => {
        return navigate("/dashboard");
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const formData = new FormData();
        const data = {
            avatar: formDetails.avatar,
            firstName: formDetails.firstName,
            lastName: formDetails.lastName,
            id: formDetails.id,
            email: formDetails.email,
            phone: formDetails.phone,
        };
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        try {
            const response = await updateUserProfileService(formData, TOKEN);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Failed to update profile. Please check your credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update profile. ${error.message}`);
            console.error('Failed to update profile:', error);
        }
    }

    useEffect(() => {
        retrieveUserProfileService(TOKEN)
            .then((data) => setFormDetails(data))
            .catch((err) => {
                console.error("Failed to fetch user profile:", err);
            });
    }, [TOKEN]);

    return (
        <Layout>
            <ProfileWrapper>
                <BaseAlertModal
                    title="Success"
                    message="Profile successfully updated"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToDashboard}
                />
                <Box
                    component={"div"}
                    className="title-box"
                >
                    <Typography
                        variant="h1"
                        fontFamily={"Inter"}
                        fontWeight={700}
                        fontSize={24}
                        lineHeight={"normal"}
                        color={"#373737"}
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Profile Overview
                    </Typography>
                    <BaseButton
                        variant="outlined"
                        border="1px solid #D7D7D7"
                        colour="#373737"
                        bgcolor="#FFFFFF"
                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                        fontSize={12}
                        fontWeight={800}
                        startIcon={<ArrowLeftIcon />}
                        onClick={handleNavigateToPrevious}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Back
                        </Typography>
                    </BaseButton>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Box
                        component={"div"}
                        className="legend-wrapper"
                    >
                        <BaseLegend>
                            Profile
                        </BaseLegend>
                        <Typography
                            variant="body1"
                            fontFamily={"Inter"}
                            fontWeight={400}
                            fontSize={13}
                            lineHeight={"normal"}
                            color={"#828683"}
                            whiteSpace={"normal"}
                            marginBlockStart={"calc(var(--basic-margin)/4)"}
                        >
                            Update your profile and personal info here.
                        </Typography>
                    </Box>
                    <BaseFieldSet
                        className="fieldset-wrapper"
                    >
                        <BaseLabel>
                            Profile Image
                        </BaseLabel>
                        <Stack
                            flex={0.7}
                            overflow={"hidden"}
                            gap={"calc(var(--flex-gap)/2)"}
                            direction={{ tablet: "row" }}
                            alignItems={{ tablet: "center" }}
                            marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="admin-avatar"
                                    className="admin-avatar"
                                />
                            ) : (
                                <img
                                    src={formDetails?.avatar || uploadImageIcon}
                                    alt="admin-avatar"
                                    className="admin-avatar"
                                />
                            )}
                            <BaseInput
                                as={"input"}
                                type="file"
                                name="avatar"
                                colour="#666666"
                                fontWeight={400}
                                inputProps={{ accept: "image/jpeg, image/png" }}
                                onChange={handleFileChange}
                            />
                        </Stack>
                    </BaseFieldSet>
                    <BaseFieldSet
                        className="fieldset-wrapper"
                    >
                        <BaseLabel>
                            Full name
                        </BaseLabel>
                        <Stack
                            flex={0.7}
                            gap={"calc(var(--flex-gap)/2)"}
                            direction={{ tablet: "row" }}
                            marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                        >
                            <BaseInput
                                required
                                fullWidth
                                name="firstName"
                                bgcolor="transparent"
                                fontWeight={400}
                                placeholder="Enter your first name"
                                value={formDetails.firstName}
                                onChange={(e) => handleChange(e)}
                            />
                            <BaseInput
                                required
                                fullWidth
                                name="lastName"
                                bgcolor="transparent"
                                fontWeight={400}
                                placeholder="Enter your last name"
                                value={formDetails.lastName}
                                onChange={(e) => handleChange(e)}
                            />
                        </Stack>
                    </BaseFieldSet>
                    <BaseFieldSet
                        className="fieldset-wrapper"
                    >
                        <BaseLabel>
                            User ID
                        </BaseLabel>
                        <Stack
                            flex={0.7}
                            gap={"calc(var(--flex-gap)/2)"}
                            direction={{ tablet: "row" }}
                            marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                        >
                            <BaseInput
                                required
                                readOnly
                                fullWidth
                                name="id"
                                bgcolor="transparent"
                                fontWeight={400}
                                placeholder="Enter your user id"
                                value={formDetails.id}
                                onChange={(e) => handleChange(e)}
                            />
                        </Stack>
                    </BaseFieldSet>
                    <BaseFieldSet
                        className="fieldset-wrapper"
                    >
                        <BaseLabel>
                            Email
                        </BaseLabel>
                        <Stack
                            flex={0.7}
                            gap={"calc(var(--flex-gap)/2)"}
                            direction={{ tablet: "row" }}
                            marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                        >
                            <BaseInput
                                required
                                readOnly
                                fullWidth
                                type="email"
                                name="email"
                                bgcolor="transparent"
                                fontWeight={400}
                                placeholder="Enter your email"
                                value={formDetails.email}
                                onChange={(e) => handleChange(e)}
                            />
                        </Stack>
                    </BaseFieldSet>
                    <BaseFieldSet
                        className="fieldset-wrapper"
                    >
                        <BaseLabel>
                            Phone number
                        </BaseLabel>
                        <Stack
                            flex={0.7}
                            gap={"calc(var(--flex-gap)/2)"}
                            direction={{ tablet: "row" }}
                            marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                        >
                            <BaseInput
                                required
                                fullWidth
                                type="phone"
                                name="phone"
                                bgcolor="transparent"
                                fontWeight={400}
                                placeholder="Enter your phone number"
                                value={formDetails.phone}
                                onChange={(e) => handleChange(e)}
                            />
                        </Stack>
                    </BaseFieldSet>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        marginBlockStart={"var(--basic-margin)"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        component={"div"}
                        className="button-box"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            radius="7px"
                            fontSize={16}
                            fontWeight={800}
                            bgcolor="#791377"
                            padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </ProfileWrapper>
        </Layout>
    )
}